import { json, type LoaderFunctionArgs } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { z } from 'zod';
import { getArchiProLinkPreview } from '~/modules/chat/api/get-archipro-link-preview.server';
import { getExternalLinkPreview } from '~/modules/chat/api/get-external-link-preview.server';
import type { LinkPreview } from '~/modules/chat/types';

export const CHAT_LINK_PREVIEW_API = '/remix-api/chat/link-preview';

const QUERY_SCHEMA = z.object({
    url: z
        .array(z.string().trim().url())
        .min(1)
        .transform((urls) => {
            // Limit to 100 to prevent abuse.
            if (urls.length > 100) {
                return urls.slice(0, 100);
            }
            return urls;
        }),
});

/**
 * API endpoint to resolve a URL to a chat link preview payload.
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        url: url.searchParams.getAll('url'),
    });

    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const previews = await Promise.all(
            result.data.url.map(async (itemUrl) => {
                // Try to parse as an ArchiPro directory item first.
                const archiProPreview = await getArchiProLinkPreview({
                    context,
                    url: itemUrl,
                });
                if (archiProPreview) {
                    return archiProPreview;
                }
                // If not, then parse as a normal URL, including ArchiPro URLs.
                return await getExternalLinkPreview({
                    context,
                    url: itemUrl,
                });
            }),
        );

        const previewsMap = previews.reduce<Record<string, LinkPreview | null>>(
            (acc, preview, index) => {
                const key = result.data.url[index] ?? '';
                acc[key] = preview;
                return acc;
            },
            {},
        );

        return json({ previews: previewsMap });
    } catch (error) {
        logger.error(`Error in ${CHAT_LINK_PREVIEW_API}`, {
            error,
            urls: result.data.url,
        });
        return json(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
